import { UIEvent, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UPDropdownItem } from 'components/dropdown';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import {
  Box,
  Checkbox,
  FormHelperText,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  GENERAL__CLEAR_SELECTION,
  GENERAL__FILTER,
  GENERAL__NO_ITEMS,
  GENERAL__SELECT_ALL,
} from 'translations/constants';
import { SelectedChips } from './SelectedChip';
import ListboxRow, { ROW_HEIGHT } from './ListboxRow';
import { ListboxWithCheckboxesProps } from './types';

const DEFAULT_LIST_HEIGHT = 240;
// Rows kept mounted above and below the visible slice, so a fast scroll doesn't flash empty space.
const OVERSCAN = 6;
// Below this many rows, mounting the whole list is cheaper than the windowing bookkeeping.
const VIRTUALIZE_FROM = 80;

const ListboxWithCheckboxes = (props: ListboxWithCheckboxesProps): JSX.Element => {
  const { options, value, onChange, label, searchable, required, helperText, enableBulkActions, maxListHeight } = props;
  const [t] = useTranslation();
  const [query, setQuery] = useState<string>('');
  const [scrollTop, setScrollTop] = useState<number>(0);
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [options, query]);

  // Set lookup rather than Array.includes: the row loop below asks this once per row, which made
  // every render O(n^2) over the full option list.
  const selectedSet = useMemo(() => new Set(value), [value]);

  // Select all acts on what the filter currently shows, so it never changes options the user cannot see.
  const selectableIds = useMemo(() => filtered.filter(o => !o.isDisabled).map(o => o.value), [filtered]);
  const isAllSelected = selectableIds.length > 0 && selectableIds.every(id => selectedSet.has(id));

  // The live selection is read through a ref so that toggle keeps a stable identity and the
  // memoised rows don't all re-render every time the selection changes.
  const valueRef = useRef(value);
  valueRef.current = value;

  const toggle = useCallback(
    (id: UPDropdownItem['value']) => {
      const { current } = valueRef;
      onChange(current.includes(id) ? current.filter(v => v !== id) : [...current, id]);
    },
    [onChange],
  );

  const toggleAll = () => {
    onChange(
      isAllSelected
        ? value.filter(v => !selectableIds.includes(v))
        : [...value, ...selectableIds.filter(id => !selectedSet.has(id))],
    );
  };

  const clear = () => onChange([]);

  const selectedOptions = useMemo(() => options.filter(o => selectedSet.has(o.value)), [options, selectedSet]);
  const showError = required && value.length === 0;

  const listHeight = maxListHeight ?? DEFAULT_LIST_HEIGHT;
  const isVirtual = filtered.length > VIRTUALIZE_FROM;

  const slice = useMemo(() => {
    if (!isVirtual) return { start: 0, end: filtered.length, padTop: 0, padBottom: 0 };
    const visible = Math.ceil(listHeight / ROW_HEIGHT) + OVERSCAN * 2;
    // Clamp, because a shrinking option list (switching the modal's type, or filtering) can leave
    // the tracked offset past the new end, which would window down to a blank slice.
    const maxStart = Math.max(0, filtered.length - visible);
    const start = Math.min(maxStart, Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN));
    const end = Math.min(filtered.length, start + visible);
    return { start, end, padTop: start * ROW_HEIGHT, padBottom: (filtered.length - end) * ROW_HEIGHT };
  }, [isVirtual, filtered.length, scrollTop, listHeight]);

  const onListScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      if (isVirtual) setScrollTop(event.currentTarget.scrollTop);
    },
    [isVirtual],
  );

  const onQueryChange = (next: string) => {
    setQuery(next);
    // A narrower result set can leave the container scrolled past its own end, which would window
    // down to an empty slice. Reset both the DOM offset and the tracked one.
    setScrollTop(0);
    if (listRef.current) listRef.current.scrollTop = 0;
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack spacing={1.25}>
        {(label || searchable || enableBulkActions) && (
          <Stack direction="row" alignItems="center" spacing={1}>
            {label && (
              <Typography variant="subtitle2" sx={{ flex: 1 }}>
                {label}
              </Typography>
            )}
            {enableBulkActions && (
              <Stack direction="row" spacing={1}>
                <Checkbox
                  size="small"
                  indeterminate={!isAllSelected && value.length > 0}
                  checked={isAllSelected}
                  onChange={toggleAll}
                  inputProps={{ 'aria-label': t(GENERAL__SELECT_ALL) }}
                />
                <Tooltip title={t(GENERAL__CLEAR_SELECTION)}>
                  <IconButton size="small" onClick={clear} aria-label={t(GENERAL__CLEAR_SELECTION)}>
                    <ClearAllIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          </Stack>
        )}

        {searchable && (
          <TextField
            size="small"
            placeholder={t(GENERAL__FILTER)}
            value={query}
            onChange={e => onQueryChange(e.target.value)}
            inputProps={{ 'aria-label': t(GENERAL__FILTER) }}
          />
        )}

        {showError && <FormHelperText error>{helperText ?? 'At least one item is required'}</FormHelperText>}

        <Box
          ref={listRef}
          onScroll={onListScroll}
          role="listbox"
          aria-multiselectable
          sx={{
            border: theme => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            overflow: 'auto',
            maxHeight: listHeight,
          }}
        >
          <List disablePadding>
            {slice.padTop > 0 && <Box component="li" aria-hidden sx={{ height: slice.padTop }} />}
            {filtered.slice(slice.start, slice.end).map((opt, i) => (
              <ListboxRow
                key={opt.value}
                option={opt}
                checked={selectedSet.has(opt.value)}
                onToggle={toggle}
                posInSet={slice.start + i + 1}
                setSize={filtered.length}
              />
            ))}
            {slice.padBottom > 0 && <Box component="li" aria-hidden sx={{ height: slice.padBottom }} />}
            {filtered.length === 0 && (
              <ListItem dense>
                <ListItemText primary={t(GENERAL__NO_ITEMS)} />
              </ListItem>
            )}
          </List>
        </Box>

        <SelectedChips selected={selectedOptions} onRemove={id => toggle(id)} />
      </Stack>
    </Paper>
  );
};

export default ListboxWithCheckboxes;
